import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useApi,
  type Home,
  type ExecutionEvent,
  type ProvisioningStep,
  type HomeIntegrations,
  type ProviderIntegration,
  type HomeMember,
  type IntegrationDetails,
  type EdgeInfo,
  type LatestRelease,
} from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { StepTimeline } from '../components/StepTimeline';
import { AddressCard } from '../components/AddressCard';
import { CamerasList } from '../components/CamerasList';
import { PairEdgeButton } from '../components/PairEdgeButton';
import { Stage2MigrationControl } from '../components/Stage2MigrationControl';
import { formatRelative, heartbeatColor, shaUrl } from '../lib/edgeStatus';
import { EdgeReleaseBadge, EdgeReleaseDetails } from '../components/EdgeReleaseStatus';
import {
  ChevronLeft, RefreshCw, RotateCcw, Trash2, Terminal, Loader2,
  Puzzle, ShieldCheck, ShieldAlert, Users, UserPlus, X, Crown,
  ChevronDown, ChevronRight as ChevronRightIcon, Save, Eye, EyeOff,
  GitCommit, ExternalLink, Globe, Network, Server, Tag,
} from 'lucide-react';

export function HomeDetailPage() {
  const { homeId = '' } = useParams();
  const api = useApi();
  const [home, setHome] = useState<Home | null>(null);
  const [steps, setSteps] = useState<ProvisioningStep[]>([]);
  const [history, setHistory] = useState<ExecutionEvent[]>([]);
  const [integrations, setIntegrations] = useState<HomeIntegrations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tearingDown, setTearingDown] = useState(false);
  const [showRawLog, setShowRawLog] = useState(false);
  const [members, setMembers] = useState<HomeMember[]>([]);
  const [memberError, setMemberError] = useState<string | null>(null);
  const [memberAddInProgress, setMemberAddInProgress] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [edges, setEdges] = useState<EdgeInfo[]>([]);
  const [edgeStatusUnavailable, setEdgeStatusUnavailable] = useState(false);
  const [latestRelease, setLatestRelease] = useState<LatestRelease | null>(null);

  const refresh = async () => {
    try {
      const { home, steps, history, edges, edgeStatusUnavailable } = await api.getHome(homeId);
      setHome(home);
      setSteps(steps ?? []);
      setHistory(history ?? []);
      setEdges(edges ?? []);
      setEdgeStatusUnavailable(edgeStatusUnavailable === true);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Integrations are fetched separately + less often — they require a cross-account
  // sts:AssumeRole + SSM scan and don't change second-by-second.
  const refreshIntegrations = async () => {
    try {
      const data = await api.listIntegrations(homeId);
      setIntegrations(data);
    } catch {
      /* non-fatal — leave previous data on screen */
    }
  };

  const refreshMembers = async () => {
    try {
      const { members } = await api.listMembers(homeId);
      setMembers(members);
    } catch (e: any) {
      setMemberError(e.message);
    }
  };

  useEffect(() => {
    refresh();
    refreshIntegrations();
    refreshMembers();
    // Latest published SHA is fleet-wide and changes only on a publish-edge merge to main
    // — fetch once on mount and refresh every minute. Enough to keep the drift badge fresh
    // without hammering S3 on every 3s home poll.
    api.getLatestRelease().then(setLatestRelease).catch(() => { /* non-fatal */ });
    const id = setInterval(refresh, 3000);
    const integrationsId = setInterval(refreshIntegrations, 30_000);
    const releaseId = setInterval(() => {
      api.getLatestRelease().then(setLatestRelease).catch(() => { /* non-fatal */ });
    }, 60_000);
    return () => {
      clearInterval(id);
      clearInterval(integrationsId);
      clearInterval(releaseId);
    };
  }, [homeId]);

  const onTeardown = async (mode: 'soft' | 'hard' = 'soft') => {
    const acct = home?.accountIds?.test;
    const msg =
      mode === 'hard'
        ? `HARD TERMINATE will destroy all infrastructure in account ${acct} AND close the AWS account itself (90-day cooldown then permanent). The next home created with this address will get a brand-new account. Continue?`
        : `This will destroy ALL infrastructure in account ${acct} for "${home?.displayName}". The AWS account remains for reuse. Continue?`;
    if (!confirm(msg)) return;
    setTearingDown(true);
    try {
      await api.teardownHome(homeId, 'test', mode);
      await refresh();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setTearingDown(false);
    }
  };

  const onAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newMemberEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setMemberError('Enter a valid email');
      return;
    }
    setMemberAddInProgress(true);
    setMemberError(null);
    try {
      await api.addMember(homeId, { email, role: 'member' });
      setNewMemberEmail('');
      await refreshMembers();
    } catch (e: any) {
      setMemberError(e.message);
    } finally {
      setMemberAddInProgress(false);
    }
  };

  const onRemoveMember = async (m: HomeMember) => {
    if (!confirm(`Remove ${m.email} from "${home?.displayName}"?`)) return;
    try {
      await api.removeMember(homeId, m.userId);
      await refreshMembers();
    } catch (e: any) {
      setMemberError(e.message);
    }
  };

  if (loading && !home) return <div className="text-slate-600">Loading…</div>;
  if (!home) return <div className="text-alert-breach">{error ?? 'Home not found'}</div>;

  const teardownAllowed = home.allowFullTeardown?.test === true && !!home.accountIds?.test;
  // Retry is now done via the local script — same command as the initial provision.
  const showRetry = home.status === 'failed' || home.status === 'teardown_failed';

  return (
    <div>
      <Link to="/" className="inline-flex items-center text-sm text-slate-600 hover:text-brand-ink mb-4">
        <ChevronLeft size={16} /> Back to homes
      </Link>

      <div className="flex items-baseline justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{home.displayName}</h1>
          <div className="text-slate-600 font-mono text-sm mt-1">{home.homeId}</div>
        </div>
        <div className="flex items-center gap-2">
          {showRetry && (
            <span className="text-xs text-amber-700 px-3 py-1.5 rounded-lg border border-amber-200 bg-amber-50 inline-flex items-center gap-1.5">
              <RotateCcw size={14} /> retry queued — runner will re-run automatically
            </span>
          )}
          <button
            onClick={refresh}
            className="text-slate-600 hover:text-brand-ink inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-slate-300 hover:border-slate-400"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        <Card label="Status"><StatusBadge status={home.status} /></Card>
        <Card label="AWS account">
          <span className="font-mono text-sm">{home.accountIds?.test ?? <span className="text-slate-500">—</span>}</span>
        </Card>
        <Card label="Region">{home.region || '—'}</Card>
        <Card label="Owner">{home.ownerEmail || '—'}</Card>
        <Card label="Created">{home.createdAt?.slice(0, 19).replace('T', ' ') || '—'}</Card>
      </div>

      {home.address && (
        <div className="mb-8">
          <Card label="Address">
            <AddressCard address={home.address} />
          </Card>
        </div>
      )}

      {error && (
        <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {steps.length > 0 && (
        <div className="mb-8 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-semibold text-lg text-brand-ink">Provisioning</h2>
            <StatusBadge status={home.status} />
          </div>

          {(home.status === 'pending_local_provision' || home.status === 'pending_local_destroy') && (
            <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3">
              <Loader2 size={18} className="text-amber-600 animate-spin mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium text-amber-700">
                  Queued — waiting for the runner on your Mac
                </div>
                <div className="text-xs text-ink-800 mt-1.5">
                  The portal-runner LaunchAgent picks this up within ~15s and executes the
                  {' '}
                  <span className="font-mono">
                    {home.status === 'pending_local_provision' ? 'provision-home.sh' : 'destroy-home.sh'}
                  </span>{' '}
                  script. No manual action needed if the runner is installed.
                </div>
                <details className="mt-2.5 text-xs text-slate-600">
                  <summary className="cursor-pointer hover:text-brand-ink select-none">
                    Runner not installed? Click to expand
                  </summary>
                  <div className="mt-2 pl-1">
                    Run once on this Mac:
                    <pre className="mt-1.5 text-[11px] font-mono text-brand-ink bg-slate-100 rounded p-2.5 overflow-x-auto whitespace-pre">
{`cd packages/cdk && ./scripts/portal/install-runner.sh`}
                    </pre>
                    Or run the script manually right now:
                    <pre className="mt-1.5 text-[11px] font-mono text-brand-ink bg-slate-100 rounded p-2.5 overflow-x-auto whitespace-pre">
{home.status === 'pending_local_provision'
  ? `cd packages/cdk && ./scripts/portal/provision-home.sh ${home.homeId} ${home.ownerEmail ?? ''}`
  : `cd packages/cdk && MANOR_RUNNER_AUTO=1 ./scripts/portal/destroy-home.sh ${home.homeId}`}
                    </pre>
                  </div>
                </details>
              </div>
            </div>
          )}

          <StepTimeline steps={steps} />

          {history.length > 0 && (
            <div className="mt-8 border-t border-slate-200 pt-4">
              <button
                onClick={() => setShowRawLog(!showRawLog)}
                className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-brand-ink"
              >
                <Terminal size={12} />
                {showRawLog ? 'Hide' : 'Show'} legacy Step Functions log ({history.length} events)
              </button>
              {showRawLog && (
                <pre className="mt-3 text-[11px] font-mono bg-slate-100 rounded-lg p-3 max-h-80 overflow-auto leading-relaxed">
                  {history.map((e, i) => (
                    <div key={i} className="flex gap-3 text-slate-600">
                      <span className="text-slate-500 shrink-0">{e.timestamp?.slice(11, 19)}</span>
                      <span className="text-slate-600 shrink-0 w-44">{e.type}</span>
                      {e.stateName && <span className="text-slate-500">{e.stateName}</span>}
                      {e.error && <span className="text-alert-breach">{e.error}</span>}
                    </div>
                  ))}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      {edges.length > 0 && (
        <EdgeSoftwareCard homeId={homeId} edges={edges} latestRelease={latestRelease} statusUnavailable={edgeStatusUnavailable} />
      )}

      <div className="mb-8 bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-semibold text-lg text-brand-ink inline-flex items-center gap-2">
            <Users size={18} className="text-slate-500" /> Members
          </h2>
          <span className="text-xs text-slate-600">{members.length} {members.length === 1 ? 'member' : 'members'}</span>
        </div>
        {memberError && (
          <div className="mb-3 p-2.5 rounded bg-red-50 border border-red-200 text-red-700 text-xs">
            {memberError}
          </div>
        )}
        <ul className="divide-y divide-slate-200 mb-4">
          {members.map((m) => (
            <li key={m.userId} className="py-2.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-brand-ink inline-flex items-center gap-2">
                  {m.role === 'owner' && <Crown size={13} className="text-slate-500" />}
                  <span className="truncate">{m.email}</span>
                  {m.pending && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700">
                      pending
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                  {m.role}{m.addedAt ? ` · added ${m.addedAt.slice(0, 10)}` : ''}
                </div>
              </div>
              {m.role !== 'owner' && (
                <button
                  onClick={() => onRemoveMember(m)}
                  className="text-slate-500 hover:text-alert-breach p-1 rounded hover:bg-red-50"
                  title={`Remove ${m.email}`}
                >
                  <X size={14} />
                </button>
              )}
            </li>
          ))}
          {members.length === 0 && (
            <li className="py-3 text-xs text-slate-600">No members yet.</li>
          )}
        </ul>
        <form onSubmit={onAddMember} className="flex gap-2">
          <input
            type="email"
            placeholder="email to invite"
            value={newMemberEmail}
            onChange={(e) => setNewMemberEmail(e.target.value)}
            className="flex-1 bg-white border border-slate-300 rounded px-3 py-1.5 text-sm text-brand-ink placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
          />
          <button
            type="submit"
            disabled={memberAddInProgress}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-slate-100 border border-slate-200 text-ink-800 hover:bg-slate-200 text-sm disabled:opacity-50"
          >
            <UserPlus size={14} /> {memberAddInProgress ? 'Sending…' : 'Add member'}
          </button>
        </form>
        <p className="text-xs text-slate-600 mt-2">
          Adding a member sends them an email with TestFlight install instructions and gives them
          access to this home in the MANOR iOS app once they sign in with their Apple ID.
        </p>
      </div>

      <CamerasList homeId={homeId} />

      {integrations && integrations.providers.length > 0 && (
        <div className="mb-8 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
            <h2 className="font-semibold text-lg text-brand-ink inline-flex items-center gap-2">
              <Puzzle size={18} className="text-slate-500" /> Integrations
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-600">
                {integrations.edgePaired ? 'edge paired' : 'edge not paired'}
              </span>
              <PairEdgeButton homeId={homeId} alreadyPaired={!!integrations.edgePaired} />
            </div>
          </div>
          <IntegrationsList integrations={integrations} homeId={homeId} />
        </div>
      )}

      {/* If integrations haven't loaded yet (or there are none) the user still needs a way to
          kick off pairing on a fresh home. Render a standalone Edge card in that case. */}
      {(!integrations || integrations.providers.length === 0) && (
        <div className="mb-8 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-baseline justify-between mb-2 gap-3 flex-wrap">
            <h2 className="font-semibold text-lg text-brand-ink inline-flex items-center gap-2">
              <Puzzle size={18} className="text-slate-500" /> Edge
            </h2>
            <PairEdgeButton homeId={homeId} alreadyPaired={!!integrations?.edgePaired} />
          </div>
          <p className="text-xs text-slate-600">
            Pair an edge box at this home to enable cameras, alarm, water meter, and Matter devices.
          </p>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="font-semibold text-lg text-brand-ink mb-2">Danger zone</h2>
        <p className="text-sm text-ink-800 mb-4">
          Tearing down destroys all CloudFormation stacks in the test account but keeps the
          account itself (so it can be re-provisioned). Disabled for prod homes.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onTeardown('soft')}
            disabled={!teardownAllowed || tearingDown}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
          >
            <Trash2 size={14} /> {tearingDown ? 'Tearing down…' : 'Tear down (keep account)'}
          </button>
          <button
            onClick={() => onTeardown('hard')}
            disabled={!teardownAllowed || tearingDown}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-100 border border-red-300 text-red-800 hover:bg-red-200 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
            title="Closes the AWS account permanently (90-day cooldown). Next home at the same address gets a new account."
          >
            <Trash2 size={14} /> Hard terminate (close account)
          </button>
        </div>
        {!teardownAllowed && (
          <div className="text-xs text-slate-600 mt-2">
            Disabled — {home.accountIds?.test ? 'allowFullTeardown.test=false' : 'no test account provisioned yet'}.
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] text-slate-600 uppercase tracking-[0.16em] mb-1.5">{label}</div>
      <div className="text-sm text-brand-ink">{children}</div>
    </div>
  );
}

const PROVIDER_LABELS: Record<string, string> = {
  edge: 'Edge box',
  nest: 'Nest (Matter)',
  hue: 'Philips Hue',
  dyson: 'Dyson',
  tesla: 'Tesla',
  kia: 'Kia Connect',
  ytmusic: 'YouTube Music',
  airthings: 'Airthings',
  bhyve: 'Orbit B-hyve',
  lg: 'LG ThinQ',
  seam: 'Seam',
  tplink: 'TP-Link',
  google: 'Google',
  garage: 'Garage Door',
};

function IntegrationsList({ integrations, homeId }: { integrations: HomeIntegrations; homeId: string }) {
  // Edge first, then everything else alphabetical.
  const sorted = [...integrations.providers].sort((a, b) => {
    if (a.provider === 'edge') return -1;
    if (b.provider === 'edge') return 1;
    return a.provider.localeCompare(b.provider);
  });

  return (
    <ul className="divide-y divide-slate-200">
      {sorted.map((p) => (
        <IntegrationRow key={p.provider} provider={p} homeId={homeId} />
      ))}
    </ul>
  );
}

function IntegrationRow({ provider, homeId }: { provider: ProviderIntegration; homeId: string }) {
  const api = useApi();
  const isEdge = provider.provider === 'edge';
  const hasParams = provider.fieldCount > 0;
  const configured = isEdge ? provider.paired === true : hasParams;
  // Edge has no editable schema (the box manages its own config). Everything else is editable.
  const editable = !isEdge;

  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState<IntegrationDetails | null>(null);
  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const styles = configured
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-slate-50 text-slate-600 border-slate-200';
  const Icon = configured ? ShieldCheck : ShieldAlert;
  const label = configured ? 'configured' : isEdge ? 'not paired' : 'no credentials';

  const loadDetails = async () => {
    if (!editable) return;
    setLoading(true);
    setError(null);
    try {
      const d = await api.getIntegration(homeId, provider.provider);
      setDetails(d);
      setDraft({}); // start empty; only changed fields get sent
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onToggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && editable && !details) {
      await loadDetails();
    }
  };

  const onSave = async () => {
    if (!details || Object.keys(draft).length === 0) {
      setExpanded(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await api.updateIntegration(homeId, provider.provider, draft);
      const summary = result.updates
        .filter((u) => u.action !== 'unchanged')
        .map((u) => `${u.key}: ${u.action}`)
        .join(', ');
      setSavedNote(summary || 'no changes');
      setDraft({});
      await loadDetails(); // refresh to show new isSet values
      setTimeout(() => setSavedNote(null), 3000);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="py-3">
      <div
        className={`flex items-center gap-4 ${editable ? 'cursor-pointer hover:bg-slate-100 -mx-2 px-2 rounded' : ''}`}
        onClick={editable ? onToggle : undefined}
      >
        {editable && (
          <span className="text-slate-600">
            {expanded ? <ChevronDown size={16} /> : <ChevronRightIcon size={16} />}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-brand-ink">
            {PROVIDER_LABELS[provider.provider] ?? provider.provider}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {isEdge ? (
              provider.paired ? (
                <>
                  {provider.hostname ? `${provider.hostname} · ` : ''}
                  {provider.publicIp ? `${provider.publicIp} · ` : ''}
                  {provider.version ?? 'paired'}
                </>
              ) : (
                'No edge box paired with this home yet.'
              )
            ) : (
              `${provider.fieldCount} ${provider.fieldCount === 1 ? 'param' : 'params'} stored in SSM`
            )}
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-1 rounded border ${styles}`}
        >
          <Icon size={12} /> {label}
        </span>
      </div>

      {expanded && editable && (
        <div className="mt-3 ml-7 p-4 rounded-lg bg-slate-50 border border-slate-200">
          {loading && <div className="text-xs text-slate-600">Loading…</div>}
          {error && (
            <div className="mb-3 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-xs">
              {error}
            </div>
          )}
          {details && (
            <>
              {details.fields.length === 0 ? (
                <div className="text-xs text-slate-600">No editable fields for this provider.</div>
              ) : (
                <div className="space-y-3">
                  {details.fields.map((f) => (
                    <FieldEditor
                      key={f.key}
                      field={f}
                      draftValue={draft[f.key]}
                      onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))}
                      onClear={() => setDraft((d) => ({ ...d, [f.key]: null }))}
                    />
                  ))}
                </div>
              )}
              {savedNote && (
                <div className="mt-3 text-xs text-emerald-700">Saved · {savedNote}</div>
              )}
              <div className="mt-4 flex gap-2 justify-end">
                <button
                  onClick={() => { setExpanded(false); setDraft({}); setSavedNote(null); }}
                  className="px-3 py-1.5 rounded text-xs text-slate-600 hover:text-brand-ink border border-slate-300 hover:border-slate-400"
                >
                  Cancel
                </button>
                <button
                  onClick={onSave}
                  disabled={saving || Object.keys(draft).length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs bg-slate-100 border border-slate-200 text-ink-800 hover:bg-slate-200 disabled:opacity-40"
                >
                  <Save size={12} /> {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </li>
  );
}

function FieldEditor({
  field,
  draftValue,
  onChange,
  onClear,
}: {
  field: IntegrationDetails['fields'][number];
  draftValue: string | null | undefined;
  onChange: (v: string) => void;
  onClear: () => void;
}) {
  const isDraft = draftValue !== undefined;
  const displayValue = isDraft ? (draftValue ?? '') : (field.value ?? '');
  const placeholderForEncrypted =
    field.encrypted && field.isSet && !isDraft ? '•••••••••••• (set)' : (field.placeholder ?? '');
  const isPasswordType = field.type === 'password';
  // Show/hide toggle is only useful for non-encrypted password fields where the value is
  // actually loaded into the form. Encrypted fields never round-trip the value back to the
  // browser, so there's nothing to reveal.
  const canReveal = isPasswordType && !field.encrypted;
  const [revealed, setRevealed] = useState(false);
  const inputType = isPasswordType && !revealed ? 'password' : 'text';

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <label className="text-xs font-medium text-brand-ink">
          {field.label}
          {field.required && <span className="text-alert-breach ml-1">*</span>}
        </label>
        <span className="text-[10px] text-slate-600">
          {field.encrypted ? 'encrypted' : 'plain'}{field.isSet ? ' · stored' : ''}
        </span>
      </div>
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            type={inputType}
            value={displayValue}
            placeholder={placeholderForEncrypted}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-white border border-slate-300 rounded px-2.5 py-1.5 text-xs text-brand-ink placeholder:text-slate-400 focus:border-slate-400 focus:outline-none pr-8"
          />
          {canReveal && (
            <button
              type="button"
              onClick={() => setRevealed((r) => !r)}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-brand-ink p-1 rounded"
              title={revealed ? 'Hide value' : 'Reveal value (this is a plain-text field)'}
            >
              {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
          )}
        </div>
        {field.isSet && (
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] text-red-700 hover:text-red-800 px-2 py-1.5 rounded border border-red-200 hover:bg-red-50"
            title="Delete this field from SSM"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}


function EdgeSoftwareCard({
  homeId,
  edges,
  latestRelease,
  statusUnavailable,
}: {
  homeId: string;
  edges: EdgeInfo[];
  latestRelease: LatestRelease | null;
  statusUnavailable: boolean;
}) {
  return (
    <div className="mb-8 bg-white border border-slate-200 rounded-xl p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-semibold text-lg text-brand-ink inline-flex items-center gap-2">
          <GitCommit size={18} className="text-slate-500" /> Edge software
        </h2>
        <div className="flex items-center gap-3 text-xs text-slate-600">
          {latestRelease?.manifest && (
            <span>
              Latest <a
                href={shaUrl(latestRelease.manifest.sha)}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-brand-ink hover:text-slate-500"
              >
                {latestRelease.manifest.shaShort}
              </a> ({latestRelease.channel})
            </span>
          )}
          <span>{edges.length} {edges.length === 1 ? 'edge' : 'edges'}</span>
        </div>
      </div>
      <div className="space-y-3">
        {statusUnavailable && <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">Edge release status is temporarily unavailable. Refresh to retry.</div>}
        {edges.map((e) => (
          <div key={e.edgeId} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className={`inline-block w-2 h-2 rounded-full ${heartbeatColor(e.lastHeartbeatAt)}`} />
              {e.currentSha ? (
                <a
                  href={shaUrl(e.currentSha)}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-sm text-brand-ink hover:text-slate-500 inline-flex items-center gap-1"
                  title="Open commit on GitHub"
                >
                  {e.currentShaShort ?? e.currentSha.slice(0, 8)} <ExternalLink size={11} className="text-slate-500" />
                </a>
              ) : (
                <span className="font-mono text-sm text-brand-ink">—</span>
              )}
              <EdgeReleaseBadge release={e.release} />
              {e.currentBranch && e.currentBranch !== 'stable' && (
                <span className="text-[10px] text-slate-500 uppercase tracking-wider">{e.currentBranch}</span>
              )}
              {e.lastDeployReason === 'rollback' && (
                <span className="text-[10px] text-amber-600 uppercase tracking-wider">rolled back</span>
              )}
              {e.lastDeployReason === 'containment-hold' && (
                <span className="text-[10px] text-amber-600 uppercase tracking-wider">contained · needs attention</span>
              )}
              <span className="text-xs text-slate-500 ml-auto font-mono" title={e.edgeId}>{e.edgeId?.slice(0, 8)}</span>
            </div>
            {e.currentShaSubject && (
              <div className="text-sm text-brand-ink mb-1.5 italic">"{e.currentShaSubject}"</div>
            )}
            <div className="mb-3 rounded border border-slate-200 bg-white p-3">
              <EdgeReleaseDetails release={e.release} />
            </div>
            <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-0.5 mb-2">
              {e.lastDeployAt && <span>Deployed {formatRelative(e.lastDeployAt)}</span>}
              {e.currentShaCommittedAt && <span>Committed {formatRelative(e.currentShaCommittedAt)}</span>}
              <span>Last seen {formatRelative(e.lastHeartbeatAt)}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 pt-2 border-t border-slate-200/60">
              {e.hostname && (
                <span className="inline-flex items-center gap-1.5">
                  <Server size={11} className="text-slate-500" />
                  <span className="text-slate-500">host</span>
                  <span className="font-mono text-brand-ink">{e.hostname}</span>
                </span>
              )}
              {e.version && (
                <span className="inline-flex items-center gap-1.5">
                  <Tag size={11} className="text-slate-500" />
                  <span className="text-slate-500">version</span>
                  <span className="font-mono text-brand-ink">{e.version}</span>
                </span>
              )}
              {e.lastPublicIp && (
                <span className="inline-flex items-center gap-1.5">
                  <Globe size={11} className="text-slate-500" />
                  <span className="text-slate-500">public IP</span>
                  <span className="font-mono text-brand-ink">{e.lastPublicIp}</span>
                  {e.lastPublicIpAt && (
                    <span className="text-slate-500">({formatRelative(e.lastPublicIpAt)})</span>
                  )}
                </span>
              )}
              {(e.pairedHomes?.length ?? 0) > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Network size={11} className="text-slate-500" />
                  <span className="text-slate-500">paired</span>
                  <span className="font-mono text-brand-ink">{e.pairedHomes!.join(', ')}</span>
                </span>
              )}
              {(e.firstRegisteredAt || e.registeredAt) && (
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-slate-500">first paired</span>
                  <span className="text-brand-ink">{formatRelative(e.firstRegisteredAt ?? e.registeredAt)}</span>
                </span>
              )}
            </div>
            <Stage2MigrationControl homeId={homeId} edge={e} />
          </div>
        ))}
      </div>
    </div>
  );
}
